import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '../../../../environments/environment';
import { getLogger } from '../../../core/utils';
import { DialogService, MessageType } from './dialog.service';

export const DEFAULT_LOCATION = {
  center: { lat: 41.387014, lng: 2.170051 },
  worldCenter: { lat: 20, lng: -20 },
  zoom: 2,
};

export interface NewLocation {
  address?: string;
  bounds?: google.maps.LatLngBounds;
  location: google.maps.LatLng;
}

interface CreateMapOptions {
  center?: google.maps.LatLngLiteral;
  maxZoom?: number;
  minZoom?: number;
  zoom?: number;
}

export interface GoogleMapsLibs {
  readonly core: google.maps.CoreLibrary;
  readonly geocoder: google.maps.Geocoder;
  readonly geocoding: google.maps.GeocodingLibrary;
  readonly maps: google.maps.MapsLibrary;
  readonly marker: google.maps.MarkerLibrary;
}

@Injectable({ providedIn: 'root' })
export class GoogleMapsService {
  private libs?: Promise<GoogleMapsLibs>;
  private readonly loader = new Loader({
    apiKey: environment.google.apiKey,
    version: 'weekly',
  });
  private readonly logger = getLogger('GoogleMapsService');

  constructor(private readonly dialog: DialogService) {}

  public async createMap(el: HTMLElement, options?: CreateMapOptions): Promise<google.maps.Map> {
    const libs = await this.getLibs();

    return new libs.maps.Map(el, {
      mapId: environment.google.mapId ?? 'uc-map',
      center: options?.center ?? DEFAULT_LOCATION.worldCenter,
      zoom: options?.zoom ?? DEFAULT_LOCATION.zoom,
      maxZoom: options?.maxZoom,
      minZoom: options?.minZoom,
      mapTypeControlOptions: {
        mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
      },
      fullscreenControl: false,
      streetViewControl: false,
      zoomControl: true,
    });
  }

  public async geocode(address: string): Promise<google.maps.GeocoderResult[]> {
    const libs = await this.getLibs();

    try {
      address = address.trim();

      if (address === '') {
        return [];
      }

      const res = await libs.geocoder.geocode({ address });
      return res.results;
    } catch (err) {
      if (err instanceof libs.core.MapsRequestError) {
        if (err.code === libs.geocoding.GeocoderStatus.ZERO_RESULTS) {
          this.dialog.snack('Zero results', MessageType.Info);
          return [];
        }
      }

      throw err;
    }
  }

  public async getCurrentLocation(): Promise<NewLocation> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = new google.maps.LatLng(
            position.coords.latitude,
            position.coords.longitude,
          );

          resolve({
            address: await this.reverseGeocode(location),
            location,
          });
        },
        (err) => reject(err),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 6 * 60 * 60 * 1000,
        },
      );
    });
  }

  public async getLibs(): Promise<GoogleMapsLibs> {
    if (!this.libs) {
      const [core, geocoding, maps, marker] = await Promise.all([
        this.loader.importLibrary('core'),
        this.loader.importLibrary('geocoding'),
        this.loader.importLibrary('maps'),
        this.loader.importLibrary('marker'),
      ]);

      this.libs = Promise.resolve({
        core,
        geocoder: new geocoding.Geocoder(),
        geocoding,
        maps,
        marker,
      });
    }

    return this.libs;
  }

  public isPlusCode(value: google.maps.GeocoderResult): boolean {
    return value.types.includes('plus_code');
  }

  public async reverseGeocode(location: google.maps.LatLng): Promise<string | undefined> {
    const libs = await this.getLibs();

    try {
      const res = await libs.geocoder.geocode({ location });

      if (res.results.length > 0 && !this.isPlusCode(res.results[0])) {
        return res.results[0].formatted_address;
      }
    } catch (err) {
      this.logger.error(err);
      this.dialog.snack(
        'There was an issue while reverse geocoding the location',
        MessageType.Error,
      );
    }

    return undefined;
  }
}
